import { PageHeader, LoadingState, DataTable, FilterBar, EmptyState, ActionToolbar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Card, CardContent,
  Container, Avatar, Stack, Tooltip, Chip, Alert,
  Collapse, Paper, Divider, useMediaQuery, alpha, useTheme
} from '@mui/material';
import debounce from 'lodash/debounce';
import {
  CalendarToday, Person, Schedule, Today, DateRange,
  CheckCircle, Warning, Error, ExpandMore, ExpandLess,
  FilterList, Refresh, Dashboard, Assignment, MenuBook, Close, ArrowForward, LocationOn, Info,
  FileDownload
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { toast } from 'react-toastify';
// Remove DatePicker imports as they are no longer needed for filtering
import api from '../utils/Api';
import { format, isWithinInterval, parseISO, startOfWeek, endOfWeek } from 'date-fns';

const normalizeAcademicYear = (value) =>
  String(value || '').trim().replace(/\s+to\s+/i, '-').replace(/\//g, '-');

const academicYearsMatch = (left, right) =>
  normalizeAcademicYear(left) === normalizeAcademicYear(right);

export default function TeacherAttendancePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // FIXED - handles both string and object formats safely
  const { schoolId, schoolName } = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return { schoolId: null, schoolName: "School" };

      const user = JSON.parse(raw);

      // Accept either string or object {_id}
      const sId = user.school?._id || user.school || null;
      const sName = user.school?.name || user.schoolName || "School";
      
      return { schoolId: sId, schoolName: sName };
    } catch (err) {
      console.error("Failed to read user from localStorage:", err);
      return { schoolId: null, schoolName: "School" };
    }
  })();

  const [dailyAttendance, setDailyAttendance] = useState([]);
  const [weeklyAttendance, setWeeklyAttendance] = useState([]);
  const [monthlyAttendance, setMonthlyAttendance] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState({
    daily: true,
    weekly: true,
    monthly: true,
    movements: true,
    term: true
  });
  const [teachers, setTeachers] = useState([]);
  const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);

  // Mark Attendance Modal States
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    teacherId: 'all',
    status: 'Holiday'
  });
  const [isSubmittingAttendance, setIsSubmittingAttendance] = useState(false);

  // PDF Export Modal States
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    exportType: 'Summary',
    exportWeek: ''
  });
  const [isExporting, setIsExporting] = useState(false);

  // Filtering States
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);

  const autoSelectDoneRef = React.useRef(false);
  const userSelectedAcademicYearRef = React.useRef(false);
  const [academicYear, setAcademicYear] = useState('');
  const [term, setTerm] = useState('');
  const [week, setWeek] = useState('');
  const [selectedTermId, setSelectedTermId] = useState('');

  const [allTerms, setAllTerms] = useState([]);
  const [termWeeks, setTermWeeks] = useState([]);
  const [currentTerm, setCurrentTerm] = useState(null);

  const [filters, setFilters] = useState({
    teacher: '',
    termId: '',
    startDate: new Date(), // Will be computed from week/term
    endDate: new Date()    // Will be computed from week/term
  });

  const [summary, setSummary] = useState({
    totalTeachers: 0,
    presentToday: 0,
    lateToday: 0,
    averageAttendance: 0
  });

  const [expandedSections, setExpandedSections] = useState({
    daily: true,
    weekly: true,
    monthly: true,
    movements: true
  });
  const groupedDailyAttendance = React.useMemo(() => {
    const groups = {};
    dailyAttendance.forEach(entry => {
      const d = entry.date ? entry.date.split('T')[0] : 'Unknown';
      if (!groups[d]) groups[d] = [];
      groups[d].push(entry);
    });
    return Object.keys(groups)
      .sort((a, b) => new Date(a) - new Date(b))
      .map(date => ({
        date,
        entries: groups[date]
      }));
  }, [dailyAttendance]);

  // Calculate summary statistics
  useEffect(() => {
    if (dailyAttendance.length > 0) {
      const presentToday = dailyAttendance.filter(entry =>
        entry.status === 'On Time' || entry.status === 'Present'
      ).length;
      const lateToday = dailyAttendance.filter(entry =>
        entry.status === 'Late'
      ).length;

      setSummary(prev => ({
        ...prev,
        totalTeachers: teachers.length,
        presentToday,
        lateToday,
        averageAttendance: teachers.length > 0 ? Math.round((presentToday / teachers.length) * 100) : 0
      }));
    }
  }, [dailyAttendance, teachers]);

  // Fetch Academic Years
  const fetchAcademicYears = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await api.get('/term/years', { params: { school: schoolId } });
      const years = [...new Set((res.data?.academicYears || []).map(normalizeAcademicYear))];
      if (years.length > 0) {
        const sortedYears = years.sort((a, b) => b.localeCompare(a));
        setAvailableAcademicYears(sortedYears);
      } else {
        setLoading(prev => ({ ...prev, daily: false, weekly: false, monthly: false, term: false }));
      }
    } catch (err) {
      console.error('Failed to fetch academic years:', err);
      toast.error('Unable to load academic years');
      setLoading(prev => ({ ...prev, daily: false, weekly: false, monthly: false, term: false }));
    }
  }, [schoolId]);

  // Fetch Terms
  const fetchTerms = useCallback(async () => {
    if (!schoolId) return;

    try {
      const res = await api.get('/term/all', { params: { school: schoolId } });
      const termsData = res.data?.allTerms || [];
      setAllTerms(termsData);

      const filteredTerms = academicYear
        ? termsData.filter((termOption) => academicYearsMatch(termOption.academicYear, academicYear))
        : termsData;
      const uniqueTerms = [...new Set(filteredTerms.map((termOption) => termOption.term))].sort();
      setAvailableTerms(uniqueTerms);

      if (filteredTerms.length > 0) {
        if (term) {
          const match = filteredTerms.find((termOption) => termOption.term === term);
          if (match) {
            setSelectedTermId(match._id);
          } else {
            const defaultTerm = filteredTerms.find(t => t.isCurrent) || filteredTerms[0];
            setTerm(defaultTerm.term);
            setSelectedTermId(defaultTerm._id);
          }
        } else {
          const defaultTerm = filteredTerms.find(t => t.isCurrent) || filteredTerms[0];
          setTerm(defaultTerm.term);
          setSelectedTermId(defaultTerm._id);
        }
      } else {
        setAvailableTerms([]);
        setTerm('');
        setSelectedTermId('');
      }
    } catch (err) {
      console.error('Failed to fetch terms:', err);
      toast.error('Unable to load terms');
    } finally {
      setLoading(prev => ({ ...prev, term: false }));
    }
  }, [schoolId, academicYear, term]);

  // Fetch Weeks
  const fetchTermWeeks = useCallback(async () => {
    if (!selectedTermId || !schoolId) {
      setAvailableWeeks([]);
      setTermWeeks([]);
      setLoading(prev => ({ ...prev, term: false }));
      return;
    }

    try {
      const res = await api.get('/term/weeks', {
        params: {
          termId: selectedTermId,
          school: schoolId,
          academicYear: academicYear,
          term: term
        }
      });

      const weeks = res.data?.weeks || [];
      const sortedWeeks = weeks.sort((a, b) => a.weekNumber - b.weekNumber);
      setTermWeeks(sortedWeeks);
      setCurrentTerm(res.data?.termInfo || null);

      const paramWeeks = sortedWeeks.map(w => w.label || `Week ${w.weekNumber}`);
      setAvailableWeeks(paramWeeks);

      const currentWeekObj = sortedWeeks.find(w => w.isCurrent);
      const currentWeekLabel = currentWeekObj?.label || (currentWeekObj ? `Week ${currentWeekObj.weekNumber}` : '');

      setWeek(prevWeek => {
        if (!prevWeek && currentWeekObj) {
          return currentWeekLabel;
        } else if (prevWeek && !paramWeeks.includes(prevWeek)) {
          return '';
        }
        return prevWeek;
      });
    } catch (err) {
      console.error('Failed to fetch weeks:', err);
      toast.error('Unable to load weeks');
    } finally {
      setLoading(prev => ({ ...prev, term: false }));
    }
  }, [selectedTermId, schoolId, academicYear, term]);

  // Update selectedTermId when term changes
  useEffect(() => {
    if (!term || allTerms.length === 0) {
      return;
    }

    const selectedTerm = allTerms.find(t => t.term === term && academicYearsMatch(t.academicYear, academicYear));
    if (selectedTerm) {
      setSelectedTermId(selectedTerm._id);
    }
  }, [term, academicYear, allTerms]);

  useEffect(() => {
    if (academicYear) fetchTerms();
  }, [academicYear, fetchTerms]);

  useEffect(() => {
    if (selectedTermId) fetchTermWeeks();
  }, [selectedTermId, fetchTermWeeks]);

  const fetchDaily = useCallback(async () => {
    setLoading(prev => ({ ...prev, daily: true }));
    try {
      const validStart = filters.startDate && !isNaN(new Date(filters.startDate).getTime()) ? new Date(filters.startDate) : new Date();
      const validEnd = filters.endDate && !isNaN(new Date(filters.endDate).getTime()) ? new Date(filters.endDate) : new Date();
      const params = {
        from: format(validStart, 'yyyy-MM-dd'),
        to: format(validEnd, 'yyyy-MM-dd'),
        school: schoolId
      };
      if (filters.teacher) params.teacherId = filters.teacher;
      if (filters.termId) params.termId = filters.termId;

      const res = await api.get('/attendance/teacher/admin/daily-records', { params });
      setDailyAttendance(res.data.data?.records || []); // Handle potential data structure variation
    } catch (err) {
      // toast.error('Failed to load daily records');
      console.error('Error fetching daily records:', err);
    } finally {
      setLoading(prev => ({ ...prev, daily: false }));
    }
  }, [filters, schoolId]);

  const fetchWeekly = useCallback(async () => {
    setLoading(prev => ({ ...prev, weekly: true }));
    try {
      const params = {
        // Pass range if week is selected to filter weekly view
        from: format(filters.startDate, 'yyyy-MM-dd'),
        to: format(filters.endDate, 'yyyy-MM-dd'),
        termId: filters.termId,
        school: schoolId
      };
      if (filters.teacher) params.teacherId = filters.teacher;

      const res = await api.get('/attendance/teacher/admin/weekly-summary', { params });

      const responseData = res.data.data?.attendance || res.data.data || [];
      const termWeeksData = res.data.data?.termWeeks || termWeeks;

      // Merge with weeks structure to ensure empty weeks show up if needed
      if (termWeeksData.length > 0) {
        const mergedData = termWeeksData.map(weekItem => {
          const weekStart = parseISO(weekItem.startDate);
          const weekEnd = parseISO(weekItem.endDate);

          const weekAttendance = Array.isArray(responseData) ? responseData.find(att => {
            const attDate = parseISO(att.weekStart || att.date);
            return isWithinInterval(attDate, { start: weekStart, end: weekEnd }) ||
              (att.weekNumber === weekItem.weekNumber);
          }) : null;

          return {
            weekNumber: weekItem.weekNumber,
            weekLabel: `Week ${weekItem.weekNumber}`,
            startDate: weekItem.startDate,
            endDate: weekItem.endDate,
            teacherName: weekAttendance?.teacherName ||
              (filters.teacher ?
                teachers.find(t => t._id === filters.teacher)?.user?.name :
                'All Teachers'),
            present: weekAttendance?.present || 0,
            late: weekAttendance?.late || 0,
            total: weekAttendance?.total || weekItem.instructionalDays || 6
          };
        });

        // Filter merged data to respect selected week if applicable
        if (week) {
          const selectedWeekNum = parseInt(week.replace('Week ', ''));
          if (!isNaN(selectedWeekNum)) {
            const filteredMerged = mergedData.filter(w => w.weekNumber === selectedWeekNum);
            setWeeklyAttendance(filteredMerged);
          } else {
            setWeeklyAttendance(mergedData);
          }
        } else {
          setWeeklyAttendance(mergedData);
        }
      } else {
        setWeeklyAttendance(responseData);
      }
    } catch (err) {
      // toast.error('Failed to load weekly summary');
      console.error('Error fetching weekly summary:', err);
    } finally {
      setLoading(prev => ({ ...prev, weekly: false }));
    }
  }, [filters, termWeeks, teachers, schoolId, week]);

  const fetchMonthly = useCallback(async () => {
    setLoading(prev => ({ ...prev, monthly: true }));
    try {
      const validStart = filters.startDate && !isNaN(new Date(filters.startDate).getTime()) ? new Date(filters.startDate) : new Date();
      const validEnd = filters.endDate && !isNaN(new Date(filters.endDate).getTime()) ? new Date(filters.endDate) : new Date();
      const params = {
        from: format(validStart, 'yyyy-MM-dd'),
        to: format(validEnd, 'yyyy-MM-dd'),
        school: schoolId
      };
      if (filters.teacher) params.teacherId = filters.teacher;
      if (filters.termId) params.termId = filters.termId;

      const res = await api.get('/attendance/teacher/admin/monthly-summary', { params });
      setMonthlyAttendance(res.data.data || []);
    } catch (err) {
      // toast.error('Failed to load monthly summary');
      console.error('Error fetching monthly summary:', err);
    } finally {
      setLoading(prev => ({ ...prev, monthly: false }));
    }
  }, [filters, schoolId]);

  const fetchMovements = useCallback(async () => {
    setLoading(prev => ({ ...prev, movements: true }));
    try {
      const params = { school: schoolId };
      const res = await api.get('/movements', { params });
      setMovements(res.data.movements || []);
    } catch (err) {
      console.error('Error fetching movements:', err);
    } finally {
      setLoading(prev => ({ ...prev, movements: false }));
    }
  }, [schoolId]);

  const fetchTeachers = useCallback(async () => {
    if (!schoolId) return;

    try {
      const res = await api.get('/teachers', {
        params: { school: schoolId }
      });
      setTeachers(res.data.teachers || []);
    } catch (err) {
      console.error('Error fetching teachers:', err);
      // toast.error('Failed to load teachers');
    }
  }, [schoolId]);

  const fetchAttendanceData = useCallback(() => {
    if (!schoolId) return;
    fetchDaily();
    fetchWeekly();
    fetchMonthly();
    fetchMovements();
  }, [fetchDaily, fetchWeekly, fetchMonthly, fetchMovements, schoolId]);

  useEffect(() => {
    if (!schoolId) return;

    fetchTeachers();
    fetchAcademicYears();

    if (!autoSelectDoneRef.current) {
      autoSelectDoneRef.current = true;
      // Pre-fill current term and week only once on initial mount
      api.get('/term/current-week', { params: { school: schoolId } })
        .then(res => {
          const cw = res.data?.data || res.data;
          if (userSelectedAcademicYearRef.current) return;
          if (cw?.academicYear && cw?.termName && cw?.termId) {
            setAcademicYear(normalizeAcademicYear(cw.academicYear));
            setTerm(cw.termName);
            setSelectedTermId(cw.termId);
            setWeek(cw.current > 0 ? `Week ${cw.current}` : '');
          }
        }).catch(err => console.error('Auto-select error:', err))
        .finally(() => {
          setLoading(prev => ({ ...prev, term: false }));
        });
    }
  }, [schoolId, fetchTeachers, fetchAcademicYears]);

  

  // Update Filters when selections change
  useEffect(() => {
    if (!selectedTermId) {
      return;
    }

    let start;
    let end;

    if (week) {
      const weekObj = termWeeks.find(
        (w) => w.label === week || `Week ${w.weekNumber}` === week,
      );
      if (weekObj) {
        start = new Date(weekObj.startDate);
        end = new Date(weekObj.endDate);
      }
    }

        if ((!start || !end) && currentTerm?.startDate && currentTerm?.endDate) {
      start = new Date(currentTerm.startDate);
      end = new Date(currentTerm.endDate);
    }

    if (!start || !end) {
      const termDoc = allTerms.find(t => t._id === selectedTermId);
      if (termDoc?.startDate && termDoc?.endDate) {
        start = new Date(termDoc.startDate);
        end = new Date(termDoc.endDate);
      }
    }

    if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
      setFilters((prev) => ({
        ...prev,
        termId: selectedTermId,
        startDate: start,
        endDate: end,
      }));
    }
  }, [week, selectedTermId, termWeeks, currentTerm, allTerms]);

  // Debounced fetch when filters change
  useEffect(() => {
    const handler = debounce(() => {
      if (filters.termId && filters.startDate && filters.endDate) {
        fetchAttendanceData();
      }
    }, 500);
    handler();
    return () => handler.cancel();
  }, [filters, fetchAttendanceData]);

  const applyFilters = () => {
    fetchAttendanceData();
  };

  const generateCustomPDF = async () => {
    try {
      setIsExporting(true);

      let start, end;
      if (exportOptions.exportWeek) {
        const weekObj = termWeeks.find(w => (w.label === exportOptions.exportWeek) || (`Week ${w.weekNumber}` === exportOptions.exportWeek));
        if (weekObj) {
          start = new Date(weekObj.startDate);
          end = new Date(weekObj.endDate);
        }
      }

      if (!start && currentTerm) {
        start = new Date(currentTerm.startDate);
        end = new Date(currentTerm.endDate);
      } else if (!start) {
        start = filters.startDate;
        end = filters.endDate;
      }

      const params = {
        from: format(start, 'yyyy-MM-dd'),
        to: format(end, 'yyyy-MM-dd'),
        school: schoolId,
        termId: selectedTermId
      };
      if (filters.teacher) params.teacherId = filters.teacher;

      const [dailyRes, weeklyRes, monthlyRes] = await Promise.all([
        api.get('/attendance/teacher/admin/daily-records', { params }),
        api.get('/attendance/teacher/admin/weekly-summary', { params }),
        api.get('/attendance/teacher/admin/monthly-summary', { params })
      ]);

      const fetchedDaily = dailyRes.data.data?.records || [];
      const rawWeekly = weeklyRes.data.data?.attendance || weeklyRes.data.data || [];
      const fetchedMonthly = monthlyRes.data.data || [];

      let fetchedWeekly = rawWeekly;
      const termWeeksData = weeklyRes.data.data?.termWeeks || termWeeks;
      if (termWeeksData.length > 0) {
        const mergedData = termWeeksData.map(weekItem => {
          const weekStart = parseISO(weekItem.startDate);
          const weekEnd = parseISO(weekItem.endDate);
          const weekAttendance = Array.isArray(rawWeekly) ? rawWeekly.find(att => {
            const attDate = parseISO(att.weekStart || att.date);
            return isWithinInterval(attDate, { start: weekStart, end: weekEnd }) ||
              (att.weekNumber === weekItem.weekNumber);
          }) : null;

          return {
            weekNumber: weekItem.weekNumber,
            weekLabel: `Week ${weekItem.weekNumber}`,
            startDate: weekItem.startDate,
            endDate: weekItem.endDate,
            teacherName: weekAttendance?.teacherName || (filters.teacher ? teachers.find(t => t._id === filters.teacher)?.user?.name : 'All Teachers'),
            present: weekAttendance?.present || 0,
            late: weekAttendance?.late || 0,
            total: weekAttendance?.total || weekItem.instructionalDays || 6
          };
        });

        if (exportOptions.exportWeek) {
          const selectedWeekNum = parseInt(exportOptions.exportWeek.replace('Week ', ''));
          if (!isNaN(selectedWeekNum)) {
            fetchedWeekly = mergedData.filter(w => w.weekNumber === selectedWeekNum);
          } else {
            fetchedWeekly = mergedData;
          }
        } else {
          fetchedWeekly = mergedData;
        }
      }

      const dateRange = `${format(start, 'MMM dd')} - ${format(end, 'MMM dd')}`;

      if (exportOptions.exportType === 'Daily') {
        const doc = new jsPDF();
        const reportTitle = "DAILY TEACHER ATTENDANCE SHEET";
        doc.setFontSize(20);
        doc.setTextColor(0, 0, 0);
        doc.text(reportTitle, 14, 22);
        
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(schoolName.toUpperCase(), 14, 30);
        
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(`Date/Period: ${dateRange}`, 14, 38);
        doc.text(`Generated: ${format(new Date(), 'PPP p')}`, 14, 44);
        doc.line(14, 48, 196, 48);

        if (fetchedDaily.length > 0) {
          const pdfGroups = {};
          fetchedDaily.forEach(entry => {
            const d = entry.date ? entry.date.split('T')[0] : 'Unknown';
            if (!pdfGroups[d]) pdfGroups[d] = [];
            pdfGroups[d].push(entry);
          });
          const pdfBody = [];
          Object.keys(pdfGroups).sort((a,b) => new Date(a) - new Date(b)).forEach(date => {
            const dateLabel = date === 'Unknown' ? 'Unknown Date' : format(parseISO(date), 'EEEE, MMMM do, yyyy');
            pdfBody.push([{ content: dateLabel, colSpan: 4, styles: { fillColor: [230, 236, 255], fontStyle: 'bold', textColor: [48, 63, 159] } }]);
            pdfGroups[date].forEach(entry => {
              pdfBody.push([entry.teacher?.user?.name || '—', formatTime(entry.signInTime), formatTime(entry.signOutTime), entry.status || '—']);
            });
          });
          autoTable(doc, {
            startY: 55,
            head: [['Teacher Name', 'Sign In', 'Sign Out', 'On-time / Late']],
            body: pdfBody,
            headStyles: { fillColor: [48, 63, 159], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            styles: { fontSize: 9, cellPadding: 4, minCellHeight: 12, verticalAlign: 'middle', textColor: [0, 0, 0] }
          });
          const finalY = doc.lastAutoTable.finalY + 20;
          if (finalY < 270) {
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("Headmaster/Administrator Signature: ___________________________", 14, finalY);
            doc.text("Date: ___________________________", 14, finalY + 10);
          }
        } else {
          doc.setFontSize(12);
          doc.text("No records found for the selected period.", 14, 60);
        }
        doc.save(`Daily_Attendance_Sheet_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
        toast.success("Daily Attendance Sheet generated");
        setIsExportModalOpen(false);
        return;
      }

      const doc = new jsPDF();
      const reportTitle = "Teacher Attendance Report";

      doc.setFontSize(22);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle.toUpperCase(), 14, 22);
      
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(schoolName.toUpperCase(), 14, 30);
      
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Period: ${dateRange}`, 14, 38);
      doc.text(`Generated on: ${format(new Date(), 'PPP p')}`, 14, 45);
      doc.line(14, 40, 196, 40);

      let finalY = 50;

      if (fetchedDaily.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Daily Attendance Records", 14, finalY);

        const sumGroups = {};
        fetchedDaily.forEach(entry => {
          const d = entry.date ? entry.date.split('T')[0] : 'Unknown';
          if (!sumGroups[d]) sumGroups[d] = [];
          sumGroups[d].push(entry);
        });
        const sumBody = [];
        Object.keys(sumGroups).sort((a,b) => new Date(a) - new Date(b)).forEach(date => {
          const dateLabel = date === 'Unknown' ? 'Unknown Date' : format(parseISO(date), 'EEEE, MMMM do, yyyy');
          sumBody.push([{ content: dateLabel, colSpan: 4, styles: { fillColor: [230, 236, 255], fontStyle: 'bold', textColor: [48, 63, 159] } }]);
          sumGroups[date].forEach(entry => {
            sumBody.push([entry.teacher?.user?.name || '—', entry.status || '—', formatTime(entry.signInTime), formatTime(entry.signOutTime)]);
          });
        });
        
        autoTable(doc, {
          startY: finalY + 5,
          head: [['Teacher', 'Status', 'Sign In', 'Sign Out']],
          body: sumBody,
          headStyles: { fillColor: [63, 81, 181] },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          margin: { top: 20 }
        });
        finalY = doc.lastAutoTable.finalY + 15;
      }

      if (fetchedWeekly.length > 0) {
        if (finalY > 240) { doc.addPage(); finalY = 20; }
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Weekly Attendance Summary", 14, finalY);
        
        autoTable(doc, {
          startY: finalY + 5,
          head: [['Week', 'Date Range', 'Teacher', 'Present', 'Late', 'Total', 'Rate']],
          body: fetchedWeekly.map(w => [
            w.weekLabel || `Week ${w.weekNumber}`,
            getWeekDateRange(w),
            w.teacherName || 'All Teachers',
            w.present,
            w.late,
            w.total,
            `${w.total > 0 ? Math.round((w.present / w.total) * 100) : 0}%`
          ]),
          headStyles: { fillColor: [156, 39, 176] },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          margin: { top: 20 }
        });
        finalY = doc.lastAutoTable.finalY + 15;
      }

      if (fetchedMonthly.length > 0) {
        if (finalY > 240) { doc.addPage(); finalY = 20; }
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Monthly Attendance Summary", 14, finalY);
        
        autoTable(doc, {
          startY: finalY + 5,
          head: [['Teacher', 'Month', 'Present', 'Late', 'Total', 'Rate']],
          body: fetchedMonthly.map(entry => [
            entry.teacherName || '—',
            `${getMonthName(entry.month)} ${entry.year}`,
            entry.present || 0,
            entry.late || 0,
            entry.total || 0,
            `${entry.total > 0 ? Math.round((entry.present / entry.total) * 100) : 0}%`
          ]),
          headStyles: { fillColor: [3, 169, 244] },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          margin: { top: 20 }
        });
      }

      doc.save(`Attendance_Report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success("Attendance PDF generated successfully");
      setIsExportModalOpen(false);
    } catch (err) {
      console.error("Custom PDF Gen Error:", err);
      toast.error("Failed to generate PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const handleMarkAttendance = async () => {
    try {
      setIsSubmittingAttendance(true);
      const res = await api.post('/attendance/teacher/admin/manual-attendance', {
        date: attendanceForm.date,
        teacherId: attendanceForm.teacherId,
        status: attendanceForm.status
      });

      if (res.data.status === 'success') {
        toast.success(res.data.message);
        setIsAttendanceModalOpen(false);
        fetchAttendanceData(); // Refresh the data
      }
    } catch (err) {
      console.error('Failed to mark attendance:', err);
      toast.error(err.response?.data?.message || 'Failed to mark attendance');
    } finally {
      setIsSubmittingAttendance(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return format(parseISO(dateStr), 'MMM dd, yyyy');
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '—';
    const d = new Date(timeStr);
    let hours = d.getUTCHours();
    const minutes = d.getUTCMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; 
    return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  };

  // Handle filter changes from UI
  const handleFilterChange = (name, value) => {
    if (name === 'academicYear') {
      userSelectedAcademicYearRef.current = true;
      const newYear = normalizeAcademicYear(value);
      setAcademicYear(newYear);

      // Immediately filter available terms for this year
      const filteredTerms = allTerms.filter((termOption) => academicYearsMatch(termOption.academicYear, newYear));
      const uniqueTerms = [...new Set(filteredTerms.map((termOption) => termOption.term))].sort();
      setAvailableTerms(uniqueTerms);

      // Auto-select a valid term in the newly selected academic year
      const matchingTerm = filteredTerms.find((t) => t.term === term);
      const defaultTerm = matchingTerm || filteredTerms.find((t) => t.isCurrent) || filteredTerms[0];

      if (defaultTerm) {
        setTerm(defaultTerm.term);
        setSelectedTermId(defaultTerm._id);
      } else {
        setTerm('');
        setSelectedTermId('');
      }
      setWeek('');
    } else if (name === 'term') {
      const newTerm = value;
      setTerm(newTerm);

      const matching = allTerms.find((t) => academicYearsMatch(t.academicYear, academicYear) && t.term === newTerm);
      if (matching) {
        setSelectedTermId(matching._id);
      }
      setWeek('');
    } else if (name === 'week') {
      setWeek(value);
    } else if (name === 'teacher') {
      setFilters(prev => ({ ...prev, teacher: value }));
    }
  };

  const getTimeRangeLabel = () => {
    if (week) {
      const wObj = termWeeks.find(w => w.label === week || `Week ${w.weekNumber}` === week);
      if (wObj) return `${format(parseISO(wObj.startDate), 'MMM dd')} - ${format(parseISO(wObj.endDate), 'MMM dd')}`;
    }
    return `${format(filters.startDate, 'MMM dd')} - ${format(filters.endDate, 'MMM dd')}`;
  };

  const getWeekDateRange = (week) => {
    const startDate = week.startDate || week.weekStart;
    const endDate = week.endDate || week.weekEnd;

    if (!startDate || !endDate) return '—';
    return `${format(parseISO(startDate), 'MMM dd')} - ${format(parseISO(endDate), 'MMM dd')}`;
  };

  const getMonthName = (month) => {
    const date = new Date(2000, month - 1, 1);
    return format(date, 'MMMM');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Time': return 'success';
      case 'Late': return 'warning';
      case 'Absent': return 'error';
      default: return 'default';
    }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'T';

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Mobile Card Components
  const MobileDailyCard = ({ entry }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40
              }} src={entry.teacher?.user?.profilePicture || entry.teacher?.profilePicture}>
                {getInitials(entry.teacher?.user?.name)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {entry.teacher?.user?.name || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(entry.date)}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={entry.status || '—'}
              size="small"
              color={getStatusColor(entry.status)}
              variant="filled"
              sx={{ fontWeight: 600, minWidth: 80 }}
            />
          </Stack>

          {/* Time Information - UPDATED GRID v2 */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  SIGN IN
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {formatTime(entry.signInTime)}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  SIGN OUT
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {formatTime(entry.signOutTime)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Location */}
          {entry.location?.coordinates && entry.location.coordinates.length >= 2 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                LOCATION
              </Typography>
              <Typography variant="body2">
                {`${(entry.location.coordinates[1] || 0).toFixed(4)}, ${(entry.location.coordinates[0] || 0).toFixed(4)}`}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const MobileWeeklyCard = ({ week }) => {
    const attendanceRate = week.total > 0
      ? Math.round((week.present / week.total) * 100)
      : 0;

    return (
      <Card sx={{ mb: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Header */}
            <Box>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                {week.weekLabel || 'Week'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getWeekDateRange(week)}
              </Typography>
            </Box>

            {/* Teacher */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                TEACHER
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {week.teacherName || 'All Teachers'}
              </Typography>
            </Box>

            {/* Stats - UPDATED GRID v2 */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    PRESENT
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {week.present}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    LATE
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {week.late}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    TOTAL
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {week.total}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Attendance Rate */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  ATTENDANCE RATE
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  {attendanceRate}%
                </Typography>
              </Stack>
              <Box sx={{
                width: '100%',
                bgcolor: '#e0e0e0',
                borderRadius: 1,
                overflow: 'hidden',
                height: 8
              }}>
                <Box
                  sx={{
                    width: `${attendanceRate}%`,
                    height: '100%',
                    bgcolor: attendanceRate > 80 ? theme.palette.success.main :
                      attendanceRate > 50 ? theme.palette.warning.main :
                        theme.palette.error.main,
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const MobileMonthlyCard = ({ entry }) => {
    const monthName = getMonthName(entry.month);
    const attendanceRate = entry.total > 0
      ? Math.round((entry.present / entry.total) * 100)
      : 0;

    return (
      <Card sx={{ mb: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Header */}
            <Box>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                {`${monthName} ${entry.year}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {entry.teacherName || '—'}
              </Typography>
            </Box>

            {/* Stats - UPDATED GRID v2 */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    PRESENT
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {entry.present || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    LATE
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {entry.late || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    TOTAL
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {entry.total || 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Attendance Rate */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  ATTENDANCE RATE
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  {attendanceRate}%
                </Typography>
              </Stack>
              <Box sx={{
                width: '100%',
                bgcolor: '#e0e0e0',
                borderRadius: 1,
                overflow: 'hidden',
                height: 8
              }}>
                <Box
                  sx={{
                    width: `${attendanceRate}%`,
                    height: '100%',
                    bgcolor: attendanceRate > 80 ? theme.palette.success.main :
                      attendanceRate > 50 ? theme.palette.warning.main :
                        theme.palette.error.main,
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const MobileMovementCard = ({ move }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 40, height: 40 }} src={move.teacherProfilePicture}>
                {getInitials(move.teacherName)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">{move.teacherName}</Typography>
                <Typography variant="caption" color="text.secondary">{formatDate(move.date)}</Typography>
              </Box>
            </Stack>
            <Chip
              label="Movement"
              size="small"
              color="info"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">DESTINATION</Typography>
            <Typography variant="body2" fontWeight="500">{move.destination}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">PURPOSE</Typography>
            <Typography variant="body2">{move.purpose}</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary" display="block">DEPARTURE</Typography>
              <Typography variant="body2" fontWeight="500">{move.departureTime}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary" display="block">ARRIVAL</Typography>
              <Typography variant="body2" fontWeight="500">{move.arrivalTime}</Typography>
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );

  const ExportPdfModal = () => (
    <Dialog
      open={isExportModalOpen}
      onClose={() => !isExporting && setIsExportModalOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ bgcolor: theme.palette.info.main, color: 'white' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FileDownload />
          <Typography variant="h6">Export PDF Document</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 3, mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select the timeframe for the {exportOptions.exportType === 'Daily' ? 'Daily Attendance Sheet' : 'Full Attendance Summary'}. This allows you to export historical or current records independently of your page view.
        </Typography>

        <TextField
          select
          fullWidth
          label="Select Week"
          value={exportOptions.exportWeek}
          onChange={(e) => setExportOptions({ ...exportOptions, exportWeek: e.target.value })}
          disabled={isExporting || availableWeeks.length === 0}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">All Term Weeks</MenuItem>
          {availableWeeks.map(w => (
            <MenuItem key={w} value={w}>{w}</MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={() => setIsExportModalOpen(false)} disabled={isExporting} variant="text">
          Cancel
        </Button>
        <Button 
          onClick={generateCustomPDF} 
          variant="contained" 
          color="info"
          disabled={isExporting}
          startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
        >
          {isExporting ? 'Generating...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const MovementLogsModal = () => (
    <Dialog
      open={isMovementModalOpen}
      onClose={() => setIsMovementModalOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{
        bgcolor: theme.palette.info.dark,
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MenuBook />
          <Typography variant="h6">Teacher Movement Logs</Typography>
        </Stack>
        <IconButton onClick={() => setIsMovementModalOpen(false)} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading.movements ? (
          <LoadingState />
        ) : movements.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <MenuBook sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
            <Typography color="text.secondary">No movement records found</Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', overflow: 'auto' }}>
            <DataTable><Table>
              <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>TEACHER</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>DESTINATION</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>TIMES</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>DATE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
{movements.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                {movements.map((move) => (
                  <TableRow key={move._id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 32, height: 32, fontSize: '0.8rem' }} src={move.teacherProfilePicture}>
                          {getInitials(move.teacherName)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">{move.teacherName}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {move.purpose}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{move.destination}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.dark, px: 1, borderRadius: 1 }}>
                          {move.departureTime}
                        </Typography>
                        <ArrowForward sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.dark, px: 1, borderRadius: 1 }}>
                          {move.arrivalTime}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(move.date)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></DataTable>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={() => setIsMovementModalOpen(false)} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );


  return (
    <Container
      maxWidth="xl"
      sx={{
        py: isMobile ? 2 : 4,
        px: isMobile ? 1 : 2
      }}
    >
      {/* Header */}
      <PageHeader title={<>Teacher Attendance</>} description={<>Track and manage teacher attendance records</>} actions={<><Button variant="outlined" color="info" onClick={() => {
          setExportOptions({
            exportType: 'Summary',
            exportWeek: week || ''
          });
          setIsExportModalOpen(true);
        }} startIcon={<FileDownload />} fullWidth={isMobile} size={isMobile ? "medium" : "large"}>
                        Download PDF
                      </Button>
        <Button variant="contained" color="secondary" onClick={() => setIsAttendanceModalOpen(true)} startIcon={<Assignment />} fullWidth={isMobile} size={isMobile ? "medium" : "large"}
        // Push to right on desktop
        >
                        Mark Attendance
                      </Button></>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.info.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.info.main,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <Person fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Total Teachers
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                  >
                    {summary.totalTeachers}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.success.main,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Present Today
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                  >
                    {summary.presentToday}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.warning.main,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <Warning fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Late Today
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                  >
                    {summary.lateToday}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6
            }
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <Dashboard fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Average Attendance
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                  >
                    {summary.averageAttendance}%
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
          <SummaryMetric
            onClick={() => setIsMovementModalOpen(true)}
            sx={{
              boxShadow: isMobile ? 1 : 3,
              border: `1px solid ${theme.palette.info.dark}`,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: isMobile ? 'none' : 'translateY(-4px)',
                boxShadow: isMobile ? 2 : 6,
                bgcolor: alpha(theme.palette.info.main, 0.02)
              }
            }}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.info.dark,
                    width: isMobile ? 36 : 48,
                    height: isMobile ? 36 : 48
                  }}
                >
                  <MenuBook fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "caption" : "h6"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                  >
                    Teacher Movements
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      fontWeight="bold"
                      sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                    >
                      {movements.length}
                    </Typography>
                    <Info sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.6 }} />
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>

      {/* Filter Card */}
      <FilterBar sx={{
        mb: 4,
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 2 : 2}
            sx={{ mb: 3 }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48
              }}
            >
              <FilterList fontSize={isMobile ? "small" : "medium"} />
            </Avatar>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="600"
              sx={{ fontSize: isMobile ? '1.125rem' : '1.5rem' }}
            >
              Attendance Filters
            </Typography>
          </Stack>

          {/* Filter Grid - UPDATED GRID v2 */}
          <Grid container spacing={isMobile ? 2 : 3}>

            {/* Academic Year */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Year"
                value={academicYear}
                onChange={(e) => handleFilterChange('academicYear', e.target.value)}
                disabled={availableAcademicYears.length === 0}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Year</MenuItem>
                {availableAcademicYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Term */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Term"
                value={term}
                onChange={(e) => handleFilterChange('term', e.target.value)}
                disabled={availableTerms.length === 0}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="" disabled>Select Term</MenuItem>
                {availableTerms.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Week */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Week"
                value={week}
                onChange={(e) => handleFilterChange('week', e.target.value)}
                disabled={!selectedTermId}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                helperText={week ? getTimeRangeLabel() : 'All Weeks (Full Term)'}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">All Weeks</MenuItem>
                {availableWeeks.map(w => (
                  <MenuItem key={w} value={w}>{w}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Teacher */}
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Teacher"
                value={filters.teacher}
                onChange={(e) => handleFilterChange('teacher', e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (selected === '') return 'All Teachers';
                    const selectedTeacher = teachers.find(teacher => teacher._id === selected);
                    return selectedTeacher?.user?.name || selected;
                  }
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">All Teachers</MenuItem>
                {teachers.map(teacher => (
                  <MenuItem key={teacher._id} value={teacher._id}>
                    {teacher.user?.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Action Buttons */}
            <Grid size={{ xs: 12 }}>
              <ActionToolbar
                direction={isMobile ? "column" : "row"}
                spacing={2}
                sx={{ gap: isMobile ? 1 : 2 }}
              >
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  disabled={loading.daily || loading.weekly || loading.monthly}
                  startIcon={<Refresh />}
                  fullWidth={isMobile}
                  size={isMobile ? "medium" : "large"}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    api.get('/term/current-week', { params: { school: schoolId } })
                      .then(res => {
                        const cw = res.data?.data || res.data;
                        if (cw?.academicYear && cw?.termName && cw?.termId) {
                          setAcademicYear(normalizeAcademicYear(cw.academicYear));
                          setTerm(cw.termName);
                          setSelectedTermId(cw.termId);
                          setWeek(cw.current > 0 ? `Week ${cw.current}` : '');
                          const termsForYear = allTerms.filter(t => academicYearsMatch(t.academicYear, cw.academicYear));
                          setAvailableTerms([...new Set(termsForYear.map(t => t.term))].sort());
                        }
                      }).catch(err => console.error('Reset error:', err));
                    setFilters(prev => ({ ...prev, teacher: '' }));
                  }}
                  fullWidth={isMobile}
                  size={isMobile ? "medium" : "large"}
                >
                  Reset Filters
                </Button>
                
                
              </ActionToolbar>
            </Grid>
          </Grid>
        </CardContent>
      </FilterBar>

      {/* Summary Cards - UPDATED GRID v2 */}
      

      {/* Term Info */}
      {
        currentTerm && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: isMobile ? 1 : 2 }}>
            <Typography variant="body2">
              {currentTerm.term} {currentTerm.academicYear} • {termWeeks.length} Weeks
            </Typography>
          </Alert>
        )
      }

      {/* Daily Attendance Section */}
      <Card sx={{
        mb: 4,
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('daily')}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: isMobile ? 40 : 48,
                    height: isMobile ? 40 : 48
                  }}
                >
                  <Today fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "h6" : "h6"}
                    fontWeight="600"
                    sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                  >
                    Daily Attendance Records
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  >
                    Individual teacher attendance for selected period
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {!isMobile && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<FileDownload />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExportOptions({ exportType: 'Daily', exportWeek: week || '' });
                      setIsExportModalOpen(true);
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Export Daily PDF
                  </Button>
                )}
                <IconButton size={isMobile ? "small" : "medium"}>
                  {expandedSections.daily ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          <Collapse in={expandedSections.daily}>
            {loading.daily ? (
              <LoadingState />
            ) : dailyAttendance.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <Today
                  sx={{
                    fontSize: isMobile ? 48 : 64,
                    color: 'text.secondary',
                    opacity: 0.5,
                    mb: 2
                  }}
                />
                <Typography
                  variant={isMobile ? "h6" : "h6"}
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                >
                  No daily records found
                </Typography>
                <Typography
                  variant={isMobile ? "body2" : "body2"}
                  color="text.secondary"
                  sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  No attendance records found for the selected filters
                </Typography>
              </Box>
            ) : isMobile ? (
              // Mobile View - Cards (Grouped by Date)
              <Box sx={{ p: 2 }}>
                {groupedDailyAttendance.map(group => (
                  <Box key={group.date} sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mb: 1.5, color: theme.palette.primary.main, borderBottom: '2px solid', borderColor: 'divider', pb: 0.5 }}
                    >
                      {group.date === 'Unknown' ? 'Unknown Date' : format(parseISO(group.date), 'EEEE, MMMM do, yyyy')}
                    </Typography>
                    <Stack spacing={1}>
                      {group.entries.map((entry) => (
                        <MobileDailyCard key={entry._id} entry={entry} />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              // Desktop/Tablet View - Table
              <Box sx={{
                width: '100%',
                overflow: 'auto',
                maxHeight: isTablet ? '60vh' : 'none'
              }}>
                <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                  <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TEACHER</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STATUS</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>SIGN IN TIME</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>SIGN OUT TIME</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
{groupedDailyAttendance.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                    {groupedDailyAttendance.map((group) => (
                      <React.Fragment key={group.date}>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.07) }}>
                          <TableCell colSpan={4} sx={{ py: 1.5, fontWeight: 'bold', color: theme.palette.primary.main, fontSize: '0.9rem' }}>
                            {group.date === 'Unknown' ? 'Unknown Date' : format(parseISO(group.date), 'EEEE, MMMM do, yyyy')}
                          </TableCell>
                        </TableRow>
                        {group.entries.map((entry) => (
                          <TableRow
                            key={entry._id}
                            sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                          >
                            <TableCell sx={{ py: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }} src={entry.teacher?.user?.profilePicture || entry.teacher?.profilePicture}>
                                  {getInitials(entry.teacher?.user?.name)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="500">
                                    {entry.teacher?.user?.name || '—'}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Chip
                                label={entry.status || '—'}
                                color={getStatusColor(entry.status)}
                                variant="filled"
                                sx={{ fontWeight: 600, minWidth: 80 }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2">
                                {formatTime(entry.signInTime)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2">
                                {formatTime(entry.signOutTime)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table></DataTable>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* Weekly Attendance Section */}
      <Card sx={{
        mb: 4,
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('weekly')}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    width: isMobile ? 40 : 48,
                    height: isMobile ? 40 : 48
                  }}
                >
                  <DateRange fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "h6" : "h6"}
                    fontWeight="600"
                    sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                  >
                    Weekly Attendance Summary
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  >
                    {currentTerm ?
                      `${currentTerm.term} ${currentTerm.academicYear}` :
                      'Weekly attendance overview'}
                  </Typography>
                </Box>
              </Stack>
              <IconButton size={isMobile ? "small" : "medium"}>
                {expandedSections.weekly ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>
          </Box>

          <Collapse in={expandedSections.weekly}>
            {loading.weekly ? (
              <LoadingState />
            ) : weeklyAttendance.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <DateRange
                  sx={{
                    fontSize: isMobile ? 48 : 64,
                    color: 'text.secondary',
                    opacity: 0.5,
                    mb: 2
                  }}
                />
                <Typography
                  variant={isMobile ? "h6" : "h6"}
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                >
                  No weekly records found
                </Typography>
                <Typography
                  variant={isMobile ? "body2" : "body2"}
                  color="text.secondary"
                  sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  No weekly attendance data available for the selected filters
                </Typography>
              </Box>
            ) : isMobile ? (
              // Mobile View - Cards
              <Box sx={{ p: 2 }}>
                <Stack spacing={1}>
                  {weeklyAttendance.map((week, index) => (
                    <MobileWeeklyCard key={`${week.weekNumber}-${week.startDate}-${index}`} week={week} />
                  ))}
                </Stack>
              </Box>
            ) : (
              // Desktop/Tablet View - Table
              <Box sx={{
                width: '100%',
                overflow: 'auto',
                maxHeight: isTablet ? '60vh' : 'none'
              }}>
                <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                  <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>WEEK</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>DATE RANGE</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TEACHER</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">PRESENT</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">LATE</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">TOTAL DAYS</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">ATTENDANCE RATE</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
{weeklyAttendance.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                    {weeklyAttendance.map((week, index) => {
                      const attendanceRate = week.total > 0
                        ? Math.round((week.present / week.total) * 100)
                        : 0;

                      return (
                        <TableRow
                          key={`${week.weekNumber}-${week.startDate}-${index}`}
                          sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="subtitle1" fontWeight="500">
                              {week.weekLabel || 'Week'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2">
                              {getWeekDateRange(week)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2">
                              {week.teacherName || 'All Teachers'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }} align="right">
                            <Typography variant="subtitle1" fontWeight="500">
                              {week.present}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }} align="right">
                            <Typography variant="subtitle1" fontWeight="500">
                              {week.late}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }} align="right">
                            <Typography variant="subtitle1" fontWeight="500">
                              {week.total}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }} align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1, bgcolor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                <Box
                                  sx={{
                                    width: `${attendanceRate}%`,
                                    bgcolor: attendanceRate > 80 ? theme.palette.success.main :
                                      attendanceRate > 50 ? theme.palette.warning.main :
                                        theme.palette.error.main,
                                    color: 'white',
                                    textAlign: 'center',
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                  }}
                                >
                                  {attendanceRate}%
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table></DataTable>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>

      <MovementLogsModal />
      <ExportPdfModal />

      {/* Monthly Attendance Section */}
      <Card sx={{
        mb: 4,
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('monthly')}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.info.main,
                    width: isMobile ? 40 : 48,
                    height: isMobile ? 40 : 48
                  }}
                >
                  <CalendarToday fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "h6" : "h6"}
                    fontWeight="600"
                    sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                  >
                    Monthly Attendance Summary
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  >
                    Monthly overview of teacher attendance patterns
                  </Typography>
                </Box>
              </Stack>
              <IconButton size={isMobile ? "small" : "medium"}>
                {expandedSections.monthly ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>
          </Box>

          <Collapse in={expandedSections.monthly}>
            {loading.monthly ? (
              <LoadingState />
            ) : monthlyAttendance.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <CalendarToday
                  sx={{
                    fontSize: isMobile ? 48 : 64,
                    color: 'text.secondary',
                    opacity: 0.5,
                    mb: 2
                  }}
                />
                <Typography
                  variant={isMobile ? "h6" : "h6"}
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                >
                  No monthly records found
                </Typography>
                <Typography
                  variant={isMobile ? "body2" : "body2"}
                  color="text.secondary"
                  sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  No monthly attendance data available for the selected filters
                </Typography>
              </Box>
            ) : isMobile ? (
              // Mobile View - Cards
              <Box sx={{ p: 2 }}>
                <Stack spacing={1}>
                  {monthlyAttendance.map((entry, index) => (
                    <MobileMonthlyCard
                      key={`${entry.teacherName}-${entry.year}-${entry.month}-${index}`}
                      entry={entry}
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              // Desktop/Tablet View - Table
              <Box sx={{
                width: '100%',
                overflow: 'auto',
                maxHeight: isTablet ? '60vh' : 'none'
              }}>
                <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                  <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TEACHER</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>MONTH</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">PRESENT</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">LATE</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">TOTAL DAYS</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">ATTENDANCE RATE</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
{monthlyAttendance.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                    {monthlyAttendance.map((entry, index) => {
                      const monthName = getMonthName(entry.month);
                      const attendanceRate = entry.total > 0
                        ? Math.round((entry.present / entry.total) * 100)
                        : 0;

                      return (
                        <TableRow
                          key={`${entry.teacherName}-${entry.year}-${entry.month}-${index}`}
                          sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="subtitle1" fontWeight="500">
                              {entry.teacherName || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2">
                              {`${monthName} ${entry.year}`}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }} align="right">
                            <Typography variant="subtitle1" fontWeight="500">
                              {entry.present || 0}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }} align="right">
                            <Typography variant="subtitle1" fontWeight="500">
                              {entry.late || 0}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }} align="right">
                            <Typography variant="subtitle1" fontWeight="500">
                              {entry.total || 0}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }} align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1, bgcolor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                <Box
                                  sx={{
                                    width: `${attendanceRate}%`,
                                    bgcolor: attendanceRate > 80 ? theme.palette.success.main :
                                      attendanceRate > 50 ? theme.palette.warning.main :
                                        theme.palette.error.main,
                                    color: 'white',
                                    textAlign: 'center',
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                  }}
                                >
                                  {attendanceRate}%
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table></DataTable>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>
      {/* Mark Attendance Dialog - inlined to prevent flicker */}
      <Dialog
        open={isAttendanceModalOpen}
        onClose={() => !isSubmittingAttendance && setIsAttendanceModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Assignment />
            <Typography variant="h6">Mark Manual Attendance</Typography>
          </Stack>
          <IconButton
            onClick={() => !isSubmittingAttendance && setIsAttendanceModalOpen(false)}
            sx={{ color: 'white' }}
            disabled={isSubmittingAttendance}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              value={attendanceForm.date}
              onChange={(e) => setAttendanceForm({ ...attendanceForm, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmittingAttendance}
            />

            <TextField
              select
              label="Teacher"
              fullWidth
              value={attendanceForm.teacherId}
              onChange={(e) => setAttendanceForm({ ...attendanceForm, teacherId: e.target.value })}
              disabled={isSubmittingAttendance}
            >
              <MenuItem value="all">All Teachers</MenuItem>
              {teachers.map(teacher => (
                <MenuItem key={teacher._id} value={teacher._id}>
                  {teacher.user?.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Status"
              fullWidth
              value={attendanceForm.status}
              onChange={(e) => setAttendanceForm({ ...attendanceForm, status: e.target.value })}
              disabled={isSubmittingAttendance}
            >
              <MenuItem value="Holiday">Holiday</MenuItem>
              <MenuItem value="Present">Present</MenuItem>
              <MenuItem value="Late">Late</MenuItem>
              <MenuItem value="Absent">Absent</MenuItem>
            </TextField>

            <Alert severity="info" sx={{ mt: 2 }}>
              Marking a Holiday or Absent will clear any existing sign-in times and locations for the selected date.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setIsAttendanceModalOpen(false)}
            disabled={isSubmittingAttendance}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleMarkAttendance}
            disabled={isSubmittingAttendance || !attendanceForm.date}
            startIcon={isSubmittingAttendance ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
          >
            {isSubmittingAttendance ? 'Saving...' : 'Save Attendance'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
